/// <amd-module name="typeahead/core/ABTest" />

/// <amd-dependency path="jquery" name="$"/>
/// <amd-dependency path="uitk" name="uitk"/>
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>

/// <reference path="../../typings/typeahead/uitk.d.ts" />
/// <reference path="../../typings/typeahead/dctk.d.ts" />
/// <reference path="../../typings/typeahead/ABTest.d.ts" />

import {Experiment} from "Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";

import {DefaultPluginAssembly} from "typeahead/core/plugins/DefaultPluginAssembly";
import {IPrefetchBucketAssignments} from "typeahead/core/plugins/PrefetchLoader";
import {SiteParam, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {IABTest} from "typings/typeahead/ABTest";

export interface IDctkParamsForUta {
    readonly guid: string;
    readonly tpid: number;
    readonly eapid: number;
    readonly id: number;
    readonly logToOmniture: boolean;
    readonly overrides: string;
    readonly callback: IDctkCallback;
}

interface IAbacusResponse {
    evaluatedExperiments: IEvaluatedExperiment[];
}

interface IEvaluatedExperiment {
    instanceId: number;
    id: number;
    name: string;
    value: number;
    message: {
        description: string;
        id: number;
        type: string;
    };
}

export class ABTest implements IABTest {
    private readonly persistentCookie: IPersistentCookie;
    private readonly abovCookieValue: string;
    private readonly abovMap: {[key: number]: number} ;
    private readonly abTestMap: {[expId: number]: Experiment};
    private readonly loggedTests: string[];
    private readonly abTestIdNameMap: {[expId: number]: string};
    private readonly bucketingDeferred: JQueryDeferred<void> = $.Deferred();

    /*
    Various components call ExecutionHelper which calls abTest for experiment logging / assignments. Usually assignments
    are obtained when uTA is initialized from getAllExperimentNames() but in some cases we need to obtain the assignment
    from dctk which blocks execution if dctk is not ready.

    This blocking is undesirable so we are making ExecutionHelper.shouldExecute() return a result immediately; if the
    experiment is not already assigned when shouldExecute() is called we essentially ignore it completely by locking it
    to the control bucket in addition to not logging it.

    AbTest maintains the state of the experiments so logically we are putting controlLockedExpMap here. ExecutionHelper
    decides when to execute so it will lock experiments as necessary and treat them appropriately.
     */
    private readonly controlLockedExpMap: object;

    // optional param is for testing purposes
    constructor(abovMap = null) {
        this.persistentCookie = uitk.createBrowserStorage ? uitk.createBrowserStorage("persistentCookies") : undefined;
        this.abovCookieValue = TypeaheadUtils.getCookie("abov");
        if (null != abovMap) {
            this.abovMap = abovMap;
        } else {
            this.abovMap = TypeaheadUtils.getAbovMapFromCookie(this.abovCookieValue);
        }
        this.abTestMap = {};
        this.abTestIdNameMap = {};
        this.loggedTests = [];
        this.controlLockedExpMap = {} as string & Experiment;
    }

    /*
    Gets and Logs the variant of an Abacus Test from its ID.
     */
    public getAndLog(exp: Experiment): Experiment {
        if (exp && (exp.id || exp.name)) {
            if (exp.id && this.hasAbovExp(exp.id)) {
                return new Experiment(exp.name, exp.id, this.getAbovValue(exp.id));
            } else if (exp.id && this.hasEvaluatedExp(exp.id)) {
                this.logExperimentAsynchronously(exp.id);
            } else {
                this.lockExperimentToControl(exp);
            }
            return this.abTestMap[exp.id];
        }
        TypeaheadErrorLogger.log(new Error("Experiment info was unavailable when calling 'getAndLog'"),
            Severity.MustFix, ProblemOrigin.UniversalTypeahead);
        return new Experiment("", 0, 0);
    }

    /*
    Overrides the variant of an Abacus Experiment.

    @param expName: Mandatory - name - The experiment name
    @param variant: Mandatory - number - the variant number (0=control, 1=variant1, etc)

    Used only by test mock setup
    */
    public override(expID: number, variant: number): void {
        if (this.abTestMap[expID]) {
            this.abTestMap[expID].value = variant;
        } else {
            this.abTestMap[expID] = new Experiment("", expID, variant);
        }
    }

    public getActiveClientTests(): string {
        let activeClientTests: string = "";
        for (let i: number = 0; i < this.loggedTests.length; i++) {
            if (this.abTestMap[this.loggedTests[i]] && this.abTestMap[this.loggedTests[i]].id) {
                activeClientTests += this.abTestMap[this.loggedTests[i]].id + "."
                    + this.abTestMap[this.loggedTests[i]].value + "|";
            }
        }
        return activeClientTests ? activeClientTests.slice(0, -1) : activeClientTests;
    }

    public evaluateAllExperiments(plugins: IPluginAssembly, prefetchExperimentData: IPrefetchBucketAssignments):
        void {
        if (Object.keys(prefetchExperimentData).length !== 0) {
            this.setExperimentBuckets(prefetchExperimentData);
        } else {
            // Call back up evaluation strategy for uTA
            this.evaluateExperiments(
                this.getAllExperimentsAttribute(plugins.get(DefaultPluginAssembly.experimentIdentifier), "name"));
        }
        this.bucketingDeferred.resolve();
    }

    public getAllExperimentsAttribute(experimentalFeatures: IExperimentalFeature[], attributeName: string): string[] {
        const experimentAttributes: string[] = [];
        const getExperimentAttribute = (experiment: Experiment): void => {
            if (experimentAttributes.indexOf(experiment[attributeName]) < 0) {
                experimentAttributes.push(experiment[attributeName]);
                this.populateIDNameMap(experiment);
            }
        };
        const getExperiments = (experimentalFeature: IExperimentalFeature): void => {
            const experiments: Experiment[] = experimentalFeature.getExperiments();
            if (experiments && experiments.length) {
                experiments.forEach(getExperimentAttribute);
            }
        };
        if (experimentalFeatures && experimentalFeatures.length) {
            experimentalFeatures.forEach(getExperiments);
        }
        return experimentAttributes;
    }

    public getBucketingPromise(): JQueryPromise<void> {
        return this.bucketingDeferred.promise();
    }

    /*
     Returns the content of the "abov" cookie - all the overridden experiments for the user on the current domain.
     */

    private populateIDNameMap(experiment: Experiment): void {
        if (!this.abTestIdNameMap[experiment.id]) {
            // Create a mapping for the ID and name of the experiment
            this.abTestIdNameMap[experiment.id] = experiment.name;
        }
    }

    private getCookieValue(): string {
        return this.abovCookieValue ? this.abovCookieValue : undefined;
    }

    private setExperimentBuckets(experiments: IPrefetchBucketAssignments): void {
        const evaluatedExperimentBuckets: any = experiments || {};
        if (evaluatedExperimentBuckets && Object.keys(evaluatedExperimentBuckets).length) {
            Object.keys(evaluatedExperimentBuckets).forEach((key: string) => {
                const expId: number = Number(key);
                this.abTestMap[key] = new Experiment(this.abTestIdNameMap[key], expId, evaluatedExperimentBuckets[key]);
            });
        }
    }

    private lockExperimentToControl(experiment: Experiment): void {
        const controlLockedExp: Experiment = new Experiment(experiment.name, experiment.id, 0);
        this.abTestMap[experiment.id] = controlLockedExp;
        this.controlLockedExpMap[experiment.id] = controlLockedExp;
        TypeaheadErrorLogger.log(new Error("Locked experiment to control: "
            + experiment.id + " " + experiment.name), Severity.Negligible, ProblemOrigin.UniversalTypeahead);
    }

    private hasEvaluatedExp(expId: number): boolean {
        const key: string = String(expId);
        return this.abTestMap.hasOwnProperty(key) && ("number" === typeof this.abTestMap[expId].value);
    }

    private hasAbovExp(expId: number): boolean {
        return this.abovMap[expId] !== undefined;
    }

    private getAbovValue(expId: number): number {
        if (this.hasAbovExp(expId)) {
            // Do not allow experiment override if the experiment is not being evaluated
            if (!this.hasEvaluatedExp(expId)) {
                TypeaheadErrorLogger.log(
                    new Error("Experiment " + expId + "is not being bucketed. Override would be ignored."),
                    Severity.MustFix, ProblemOrigin.UniversalTypeahead);
            } else {
                return this.abovMap[expId];
            }
        }
        return 0;
    }

    private hasControlLockedExp(expId: number): boolean {
        const idStr: string = String(expId);
        return this.controlLockedExpMap.hasOwnProperty(idStr);
    }

    private logExperimentAsynchronously(expId: number): void {
        setTimeout(() => {
            this.logExperiment(expId);
        });
    }

    private logExperiment(expId: number): void {
        const idStr: string = String(expId);
        if (!this.hasControlLockedExp(expId) && this.loggedTests.indexOf(idStr) < 0) {
            this.loggedTests.push(idStr);
            require(["dctk/dctk"], (dctk) => {
                dctk.evaluateExperimentAndLog(this.getDctkParams(expId, true, undefined));
            });
        }
    }

    private evaluateExperiments(experiments: string[]): void {
        if (experiments && experiments.length) {
            $.ajax({
                contentType: "application/json; charset=UTF-8",
                data: JSON.stringify(this.getAbacusParams(experiments)),
                dataType: "json",
                type: "POST",
                url: "/api/bucketing/v1/evaluateExperiments",
            }).done((response: IAbacusResponse) => {
                if (response && response.evaluatedExperiments && response.evaluatedExperiments.length) {
                    response.evaluatedExperiments.forEach((exp: IEvaluatedExperiment) => {
                        this.abTestMap[exp.name] = new Experiment(exp.name, exp.id,
                            exp.message ? undefined : exp.value);
                    });
                }
            }).fail(() => {
                TypeaheadErrorLogger.log(new Error(
                    "Unable to call abacus api to evaluate experiments at page load"),
                    Severity.Excusable, ProblemOrigin.UniversalTypeahead);
            });
        }
    }

    private getDctkParams(expId: number, logToOmniture: boolean, callback: IDctkCallback): IDctkParamsForUta {
        return {
            callback,
            eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
            guid: TypeaheadUtils.getGuid(),
            id: expId,
            logToOmniture,
            overrides: this.getCookieValue(),
            tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
        };
    }

    private getAbacusParams(experiments: string[]) {
        return {
            eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
            guid: TypeaheadUtils.getGuid(),
            name: experiments,
            overrides: this.getCookieValue(),
            tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
            verbose: true,
        };
    }
}
